import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { CompanyService, CompanyRequest, CompanyResponse } from '@src/app/shared/backend/clients';
import { SessionService } from '@src/app/shared/services/session.service';
import { Currency } from '@src/app/shared/enums/Currency.enum';
import { HeaderService } from '@app/shared/services/header.service';
import { httpUrlValidator } from '@app/shared/validators/httpUrl.validator';

@Component({
    selector: 'app-company-settings',
    templateUrl: './company-settings.component.html',
    styleUrls: ['./company-settings.component.scss'],
    host: {
        class: 'app-company-settings'
    },
    standalone: false
})
export class CompanySettingsComponent implements OnInit {

  companyFormReady = false;

  showCreateCompanyModal = false;

  company: CompanyResponse | null = null;
  companyRequest!: any;

  private readonly identifierWithoutWhitespaceRegex = /^\S*$/;

  public createCompanyForm: FormGroup = this.fb.group({
    name: ['', Validators.required],
    defaultBrandId: [[]],
    identifier: ['', Validators.pattern(this.identifierWithoutWhitespaceRegex)],
    currency: [[Currency.EUR]],
    warrantyUseLimit: [0],
    logoLink: [''],
    website: ['', httpUrlValidator],
    privacyPolicyLink: ['', httpUrlValidator],
    termsOfUseLink: ['', httpUrlValidator],
    // brandPrivacyPolicyLink: [''],
    // brandTermsOfUseLink: [''],
    supportEmail: [''],
    notificationSenderEmail: [''],
    iosDownloadLink: ['', httpUrlValidator],
    androidDownloadLink: ['', httpUrlValidator],
    selectedCompany: [false],
    businessGroup: [false],
    allowSerialNumberCreationDuringSale: [false],
    isLegacy: [false],
    companyRequest: [false],
    allowPasswordChange: [false],
    showProductAttributes: [false],
    showSecurityCode: [false],
    showSecurityCodeVersion: [false],
    repairOnlySoldItems: [false],
    isMfaMandatory: [false],
    directImportEnabled: [false],
    implicitSaleCreation: [false],
    assistanceImage: [null],
    isPrepaired: ['false'],
    hasLimitedEdition: ['false'],
    hasCpo: ['false'],
    inventorySubmissionThresholdDays: [30],
    userManualLink: ['', httpUrlValidator],
    supportLink: ['', httpUrlValidator],
  });

  public updateCompanyForm: FormGroup = this.fb.group({
    name: ['', Validators.required],
    defaultBrandId: [[]],
    identifier: ['', Validators.pattern(this.identifierWithoutWhitespaceRegex)],
    currency: [[Currency.EUR]],
    warrantyUseLimit: [0],
    logoLink: [''],
    website: ['', httpUrlValidator],
    privacyPolicyLink: ['', httpUrlValidator],
    termsOfUseLink: ['', httpUrlValidator],
    // brandPrivacyPolicyLink: [''],
    // brandTermsOfUseLink: [''],
    supportEmail: [''],
    notificationSenderEmail: [''],
    iosDownloadLink: ['', httpUrlValidator],
    androidDownloadLink: ['', httpUrlValidator],
    selectedCompany: [false],
    businessGroup: [false],
    allowSerialNumberCreationDuringSale: [false],
    isLegacy: [false],
    companyRequest: [false],
    allowPasswordChange: [false],
    showProductAttributes: [false],
    showSecurityCode: [false],
    showSecurityCodeVersion: [false],
    repairOnlySoldItems: [false],
    isMfaMandatory: [false],
    assistanceImage: [null],
    isPrepaired: ['false'],
    directImportEnabled: ['false'],
    implicitSaleCreation: ['false'],
    hasLimitedEdition: ['false'],
    hasCpo: ['false'],
    inventorySubmissionThresholdDays: [30],
    userManualLink: ['', httpUrlValidator],
    supportLink: ['', httpUrlValidator],
  });

  constructor(
    private fb: FormBuilder,
    private companyService: CompanyService,
    private session: SessionService,
    private headerService: HeaderService
  ) { }

  async ngOnInit() {
    const companyResponse: CompanyResponse = await this.session.getCompanyData();
    this.company = companyResponse;

    this.session.currentCompanyData$.subscribe(company => {
      this.company = company;
    });

    this.companyRequest = {
      name: companyResponse.name ?? '',
      identifier: companyResponse.identifier ?? '',
      currency: companyResponse.currency ?? Currency.EUR,
      website: companyResponse.website ?? null,
      privacyPolicyLink: companyResponse.privacyPolicyLink ?? null,
      termsOfUseLink: companyResponse.termsOfUseLink ?? null,
      supportEmail: companyResponse.supportEmail ?? null,
      notificationSenderEmail: companyResponse.notificationSenderEmail ?? null,
      userManualLink: companyResponse.userManualLink ?? null,
      supportLink: companyResponse.supportLink ?? null,
      iosDownloadLink: companyResponse.iosDownloadLink ?? null,
      androidDownloadLink: companyResponse.androidDownloadLink ?? null,
      hasRetailerBusinessGroup: companyResponse.hasRetailerBusinessGroup ?? null,
      canCreateItemOnSale: companyResponse.canCreateItemOnSale ?? null,
      showIsLegacy: companyResponse.showIsLegacy ?? null,
      allowUserChangePassword: companyResponse.allowUserChangePassword ?? null,
      showProductAttributeDetails: companyResponse.showProductAttributeDetails ?? null,
      showSecurityCode: companyResponse.showSecurityCode ?? null,
      showSecurityCodeVersion: companyResponse.showSecurityCodeVersion ?? null,
      warrantyUseLimit: companyResponse.warrantyUseLimit ?? null,
      isMfaMandatory: companyResponse.isMfaMandatory ?? null,
      repairOnlySoldItems: companyResponse.repairOnlySoldItems ?? null,
      digitalPassportUrl: companyResponse.digitalPassportUrl ?? null,
      isPrepaired: companyResponse.isPrepaired ?? null,
      inventorySubmissionThresholdDays: companyResponse.inventorySubmissionThresholdDays ?? 30,
      directImportEnabled: companyResponse.directImportEnabled ?? null,
      implicitSaleCreation: companyResponse.implicitSaleCreation ?? null,
      hasLimitedEdition: companyResponse.hasLimitedEdition ?? null,
      hasCpo: companyResponse.hasCpo ?? null
    };

    this.setUpdateCompanyFormValues();

    this.companyFormReady = true;
  }

  private setUpdateCompanyFormValues(): void {
    this.updateCompanyForm.patchValue({
      name: this.companyRequest.name,
      identifier: this.companyRequest.identifier,
      currency: this.companyRequest.currency ? [this.companyRequest.currency] : [],
      website: this.companyRequest.website,
      privacyPolicyLink: this.companyRequest.privacyPolicyLink,
      termsOfUseLink: this.companyRequest.termsOfUseLink,
      supportEmail: this.companyRequest.supportEmail,
      notificationSenderEmail: this.companyRequest.notificationSenderEmail,
      userManualLink: this.companyRequest.userManualLink,
      supportLink: this.companyRequest.supportLink,
      iosDownloadLink: this.companyRequest.iosDownloadLink,
      androidDownloadLink: this.companyRequest.androidDownloadLink,
      businessGroup: this.companyRequest.hasRetailerBusinessGroup,
      allowSerialNumberCreationDuringSale: this.companyRequest.canCreateItemOnSale,
      isLegacy: this.companyRequest.showIsLegacy,
      allowPasswordChange: this.companyRequest.allowUserChangePassword,
      showProductAttributes: this.companyRequest.showProductAttributeDetails,
      showSecurityCode: this.companyRequest.showSecurityCode,
      showSecurityCodeVersion: this.companyRequest.showSecurityCodeVersion,
      warrantyUseLimit: this.companyRequest.warrantyUseLimit,
      isMfaMandatory: this.companyRequest.isMfaMandatory,
      repairOnlySoldItems: this.companyRequest.repairOnlySoldItems,
      inventorySubmissionThresholdDays: this.companyRequest.inventorySubmissionThresholdDays ?? 30,
      isPrepaired: this.companyRequest.isPrepaired ? 'true' : 'false',
      hasLimitedEdition: this.companyRequest.hasLimitedEdition ? 'true' : 'false',
      hasCpo: this.companyRequest.hasCpo ? 'true' : 'false',
      implicitSaleCreation: this.companyRequest.implicitSaleCreation ? 'true' : 'false',
      directImportEnabled: this.companyRequest.directImportEnabled ? 'true' : 'false'
    });
  }

  openCreateCompanyModal(): void {
    this.createCompanyForm.reset();
    this.createCompanyForm.patchValue({
      currency: [Currency.EUR],
      defaultBrandId: []
    });
    this.showCreateCompanyModal = true;
  }

  onCancelCreateCompany(): void {
    this.showCreateCompanyModal = false;
  }

  private normalizeCompanyIdentifier(form: FormGroup): void {
    const identifierControl = form.get('identifier');
    const identifier = identifierControl?.value;

    identifierControl?.setValue(identifier.trim().toLowerCase());
  }

  async onCreateCompany(): Promise<void> {
    if (this.createCompanyForm.valid) {
      const newCompanyPayload: CompanyRequest = {
        name: this.createCompanyForm.value.name,
        identifier: this.createCompanyForm.value.identifier,
        currency: this.createCompanyForm.value.currency?.[0],
        website: this.createCompanyForm.value.website?.trim() ?? null,
        privacyPolicyLink: this.createCompanyForm.value.privacyPolicyLink?.trim() ?? null,
        termsOfUseLink: this.createCompanyForm.value.termsOfUseLink?.trim() ?? null,
        supportEmail: this.createCompanyForm.value.supportEmail,
        notificationSenderEmail: this.createCompanyForm.value.notificationSenderEmail,
        userManualLink: this.createCompanyForm.value.userManualLink?.trim() ?? null,
        supportLink: this.createCompanyForm.value.supportLink?.trim() ?? null,
        iosDownloadLink: this.createCompanyForm.value.iosDownloadLink?.trim() ?? null,
        androidDownloadLink: this.createCompanyForm.value.androidDownloadLink?.trim() ?? null,
        hasRetailerBusinessGroup: this.createCompanyForm.value.businessGroup,
        canCreateItemOnSale: this.createCompanyForm.value.allowSerialNumberCreationDuringSale,
        showIsLegacy: this.createCompanyForm.value.isLegacy,
        allowUserChangePassword: this.createCompanyForm.value.allowPasswordChange,
        showProductAttributeDetails: this.createCompanyForm.value.showProductAttributes,
        showSecurityCode: this.createCompanyForm.value.showSecurityCode,
        showSecurityCodeVersion: this.createCompanyForm.value.showSecurityCodeVersion,
        repairOnlySoldItems: this.createCompanyForm.value.repairOnlySoldItems,
        isMfaMandatory: this.createCompanyForm.value.isMfaMandatory,
        digitalPassportUrl: this.createCompanyForm.value.digitalPassportUrl,
        inventorySubmissionThresholdDays: this.createCompanyForm.value.inventorySubmissionThresholdDays ?? 30,
        isPrepaired: this.createCompanyForm.value.isPrepaired === 'true' ? true : false,
        hasLimitedEdition: this.createCompanyForm.value.hasLimitedEdition === 'true' ? true : false,
        hasCpo: this.createCompanyForm.value.hasCpo === 'true' ? true : false,
        implicitSaleCreation: this.createCompanyForm.value.implicitSaleCreation === 'true' ? true : false,
        directImportEnabled: this.createCompanyForm.value.directImportEnabled === 'true' ? true : false,
        warrantyUseLimit: this.createCompanyForm.value.warrantyUseLimit,
      };

      const createdCompanyId = await firstValueFrom(this.companyService.apiCompanyCreatePost(newCompanyPayload));
      this.showCreateCompanyModal = false;

      if (createdCompanyId == null) {
        this.headerService.triggerRefreshCompanies();
        return;
      }

      // Switch to new company by companyID
      await this.session.setCompanyId(createdCompanyId);
    }
  }

  async updateCompanySettings() {
    this.normalizeCompanyIdentifier(this.updateCompanyForm);

    if (this.updateCompanyForm.valid && this.company?.id != null) {

      const company = await this.session.getCompanyData();

      console.log(company);

      const payload: CompanyRequest = {
        name: this.updateCompanyForm.value.name,
        identifier: this.updateCompanyForm.value.identifier,
        currency: this.updateCompanyForm.value.currency?.[0],
        website: this.updateCompanyForm.value.website?.trim() ?? null,
        privacyPolicyLink: this.updateCompanyForm.value.privacyPolicyLink?.trim() ?? null,
        termsOfUseLink: this.updateCompanyForm.value.termsOfUseLink?.trim() ?? null,
        supportEmail: this.updateCompanyForm.value.supportEmail,
        notificationSenderEmail: this.updateCompanyForm.value.notificationSenderEmail,
        userManualLink: this.updateCompanyForm.value.userManualLink?.trim() ?? null,
        supportLink: this.updateCompanyForm.value.supportLink?.trim() ?? null,
        iosDownloadLink: this.updateCompanyForm.value.iosDownloadLink?.trim() ?? null,
        androidDownloadLink: this.updateCompanyForm.value.androidDownloadLink?.trim() ?? null,
        hasRetailerBusinessGroup: company?.hasRetailerBusinessGroup,
        canCreateItemOnSale: company?.canCreateItemOnSale,
        showIsLegacy: company?.showIsLegacy,
        allowUserChangePassword: company?.allowUserChangePassword,
        showProductAttributeDetails: company?.showProductAttributeDetails,
        showSecurityCode: company?.showSecurityCode,
        showSecurityCodeVersion: company?.showSecurityCodeVersion,
        repairOnlySoldItems: company?.repairOnlySoldItems,
        isMfaMandatory: company?.isMfaMandatory,
        digitalPassportUrl: company?.digitalPassportUrl,
        inventorySubmissionThresholdDays: company?.inventorySubmissionThresholdDays ?? 30,
        isPrepaired: company?.isPrepaired,
        hasLimitedEdition: company?.hasLimitedEdition,
        hasCpo: company?.hasCpo,
        implicitSaleCreation: company?.implicitSaleCreation,
        directImportEnabled: company?.directImportEnabled,
        warrantyUseLimit: company?.warrantyUseLimit,
      };

      if (!company?.id) return;

      await firstValueFrom(this.companyService.apiCompanyUpdateIdPut(company.id, payload));

      const updatedCompany: CompanyResponse = {
        ...company,
        name: payload.name,
        userManualLink: payload.userManualLink,
        supportLink: payload.supportLink,
        defaultBrandId: company?.defaultBrandId
      };

      this.session.setCurrentCompanyData(updatedCompany);
    }
  }
}
